import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { api, apiErr } from "@/api";
import { useAuth } from "@/context/AuthContext";
import { Brand, PoweredBy } from "@/components/Brand";
import { toast } from "sonner";
import { ArrowLeft, Phone, ShieldCheck } from "lucide-react";

const CLASSES = ["buyer", "retailer", "trader", "vendor", "general"];

export default function VisitorLogin() {
  const [step, setStep] = useState(1);
  const [phone, setPhone] = useState("");
  const [otp, setOtp] = useState("");
  const [devOtp, setDevOtp] = useState("");
  const [name, setName] = useState("");
  const [classification, setClassification] = useState("general");
  const [consent, setConsent] = useState(true);
  const [busy, setBusy] = useState(false);
  const navigate = useNavigate();
  const { login } = useAuth();

  const requestOtp = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await api.post("/visitor/otp/request", { phone });
      setDevOtp(data.dev_otp || "");
      setStep(2);
      toast.success("OTP sent (simulated)");
    } catch (err) {
      toast.error(apiErr(err));
    } finally {
      setBusy(false);
    }
  };

  const verify = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await api.post("/visitor/otp/verify", { phone, otp, name, classification, consent });
      login(data.token, "visitor", data.visitor);
      toast.success("Welcome to JJS!");
      navigate("/visitor");
    } catch (err) {
      toast.error(apiErr(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen flex flex-col">
      <header className="max-w-md mx-auto w-full px-4 h-16 flex items-center justify-between">
        <Brand />
        <Link to="/" className="text-slate-400 hover:text-amber-300 flex items-center gap-1 text-sm" data-testid="back-home">
          <ArrowLeft size={16} /> Back
        </Link>
      </header>

      <div className="max-w-md mx-auto w-full px-4 flex-1 flex flex-col justify-center pb-16">
        <div className="glass rounded-3xl p-7 fade-up gold-glow">
          <div className="h-12 w-12 rounded-2xl btn-gold flex items-center justify-center mb-5">
            {step === 1 ? <Phone size={20} className="text-[#0B0F12]" /> : <ShieldCheck size={20} className="text-[#0B0F12]" />}
          </div>

          {step === 1 ? (
            <>
              <h1 className="font-display text-3xl gold-text">Visitor Access</h1>
              <p className="text-slate-400 text-sm mt-1">Enter your JJS-registered mobile number</p>
              <form onSubmit={requestOtp} className="space-y-4 mt-6">
                <input
                  data-testid="visitor-phone-input"
                  value={phone}
                  onChange={(e) => setPhone(e.target.value)}
                  placeholder="Mobile number"
                  inputMode="tel"
                  required
                  className="w-full bg-black/30 gold-border rounded-xl px-4 py-3 text-slate-100 placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-[var(--gold)]"
                />
                <button data-testid="visitor-request-otp" disabled={busy} className="w-full btn-gold py-3 rounded-xl disabled:opacity-60">
                  {busy ? "Sending…" : "Send OTP"}
                </button>
              </form>
            </>
          ) : (
            <>
              <h1 className="font-display text-3xl gold-text">Verify & Set Up</h1>
              <p className="text-slate-400 text-sm mt-1">Enter the OTP sent to {phone}</p>
              {devOtp && (
                <div className="mt-3 text-center text-sm bg-amber-500/10 border border-amber-500/30 rounded-xl py-2 text-amber-300" data-testid="visitor-dev-otp">
                  Simulated OTP: <span className="font-mono-j font-semibold tracking-widest">{devOtp}</span>
                </div>
              )}
              <form onSubmit={verify} className="space-y-4 mt-5">
                <input
                  data-testid="visitor-otp-input"
                  value={otp}
                  onChange={(e) => setOtp(e.target.value)}
                  placeholder="6-digit OTP"
                  inputMode="numeric"
                  required
                  className="w-full bg-black/30 gold-border rounded-xl px-4 py-3 text-slate-100 text-center tracking-[0.4em] font-mono-j focus:outline-none focus:ring-2 focus:ring-[var(--gold)]"
                />
                <input
                  data-testid="visitor-name-input"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="Your name"
                  className="w-full bg-black/30 gold-border rounded-xl px-4 py-3 text-slate-100 placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-[var(--gold)]"
                />
                <div>
                  <label className="text-xs uppercase tracking-wide text-slate-400">I am a</label>
                  <div className="grid grid-cols-3 gap-2 mt-2" data-testid="visitor-role-select">
                    {CLASSES.map((c) => (
                      <button
                        type="button"
                        key={c}
                        data-testid={`classification-${c}`}
                        onClick={() => setClassification(c)}
                        className={`capitalize text-xs py-2 rounded-lg border transition ${classification === c ? "btn-gold border-transparent" : "glass text-slate-300 border-amber-500/20"}`}
                      >
                        {c}
                      </button>
                    ))}
                  </div>
                </div>
                <label className="flex items-start gap-2 text-xs text-slate-400 cursor-pointer" data-testid="visitor-consent">
                  <input type="checkbox" checked={consent} onChange={(e) => setConsent(e.target.checked)} className="mt-0.5 accent-[var(--gold)]" />
                  I consent to exhibitors viewing my visit and contacting me per my preferences.
                </label>
                <button data-testid="visitor-verify-otp" disabled={busy} className="w-full btn-gold py-3 rounded-xl disabled:opacity-60">
                  {busy ? "Verifying…" : "Enter Exhibition"}
                </button>
              </form>
            </>
          )}
        </div>
        <div className="mt-8"><PoweredBy /></div>
      </div>
    </div>
  );
}
